import { useState } from "react";
import {
  ProductDetailDTO,
  ProductSubType,
  SubscriptionDTO,
  UpgradeType,
} from "@campgladiator/cg-common.types.types";
import { upgradeEligibleSubscriptionTypes } from "@campgladiator/cg-common.logic.subscriptions";
import { fetchProductDetails } from "@campgladiator/cg-common.api.products";

const useMembershipManagementUpgrade = () => {
  const [growlMessageDescription, setGrowlMessageDescription] =
    useState<string>("");
  const [growlMessageVisible, setGrowlMessageVisible] =
    useState<boolean>(false);
  const [areEligibleProductsLoading, setAreEligibleProductsLoading] =
    useState<boolean>(false);

  const genericErrorMessage =
    "An error has occurred. Please try again or contact our customer suppport team for help.";
  const [productDetails, setProductDetails] = useState<ProductDetailDTO[]>();
  type UsersSubscribedProductTypes = {
    productSubType: SubscriptionDTO["productSubType"];
    productType: SubscriptionDTO["productType"];
  }[];

  const filterUsersProductsEligibleForUpgrade = (
    usersSubscribedProductTypes: UsersSubscribedProductTypes
  ) =>
    usersSubscribedProductTypes.filter(({ productType }) =>
      upgradeEligibleSubscriptionTypes.includes(productType)
    );

  const getUsersUpgradeEligibleProductDetails = async (
    usersUpgradeEligibleProducts: UsersSubscribedProductTypes
  ) =>
    await Promise.all(
      usersUpgradeEligibleProducts.map(
        async ({ productType, productSubType }) =>
          await fetchProductDetails({ productType, productSubType })
      )
    );

  const getUpgradeEligibleProductDetails = async (
    usersSubscribedProductTypes: UsersSubscribedProductTypes
  ) => {
    const usersUpgradeEligibleProducts = filterUsersProductsEligibleForUpgrade(
      usersSubscribedProductTypes
    );
    const upgradableProductsDetails =
      await getUsersUpgradeEligibleProductDetails(usersUpgradeEligibleProducts);
    return upgradableProductsDetails;
  };

  const populateProductWithTypeAndSubType = (
    productDetailsArray: ProductDetailDTO[]
  ) => {
    for (const productDetails of productDetailsArray) {
      const { type, subType, products } = productDetails;
      for (const product of products) {
        product.type = type;
        product.subType = subType;
      }
    }
    return productDetailsArray;
  };

  const setUsersUpgradeEligibleProductDetails = async (
    subscriptionsList: SubscriptionDTO[],
    upgradeType: UpgradeType
  ) => {
    setAreEligibleProductsLoading(true);
    const userSubscribedProductTypes: UsersSubscribedProductTypes = [];
    try {
      if (upgradeType === "UPGRADE") {
        const onlyCurrentTypeAndSubType = subscriptionsList.map(
          ({ productType, productSubType }) => ({
            productType,
            productSubType,
          })
        );
        userSubscribedProductTypes.push(...onlyCurrentTypeAndSubType);
      } else if (upgradeType === "SWAP") {
        const online = subscriptionsList.map(({ productType }) => ({
          productType,
          productSubType: "ONLINE" as ProductSubType,
        }));
        const inPerson = subscriptionsList.map(({ productType }) => ({
          productType,
          productSubType: "IN_PERSON" as ProductSubType,
        }));
        userSubscribedProductTypes.push(...inPerson, ...online);
      }
      const upgradeEligibleProductDetails =
        await getUpgradeEligibleProductDetails(userSubscribedProductTypes);
      setProductDetails(upgradeEligibleProductDetails);
      setAreEligibleProductsLoading(false);
      return populateProductWithTypeAndSubType(upgradeEligibleProductDetails);
    } catch (error) {
      setAreEligibleProductsLoading(false);
      console.error(error);
      // Handle the error appropriately
    }
  };

  return {
    areEligibleProductsLoading,
    genericErrorMessage,
    growlMessageDescription,
    setGrowlMessageDescription,
    growlMessageVisible,
    setGrowlMessageVisible,
    setUsersUpgradeEligibleProductDetails,
    productDetails,
  };
};

export default useMembershipManagementUpgrade;
